/**
 * Minimal screen router.
 * - One screen rendered at a time into #app.
 * - Uses history.pushState so the phone's back button/gesture works.
 * - All clicks go through ONE delegated listener: elements declare data-action="name",
 *   and the current screen (or the global table) provides the handler.
 */
import { track } from '../data/track';
import { $, closeSheet, esc, icons, sheetHandler } from './dom';
import { t } from '../i18n';

export type NavTab = 'home' | 'stats' | 'profile';
export type Action = (el: HTMLElement, e: Event) => void | Promise<void>;

export interface Screen {
  id: string;
  nav?: NavTab;              // shows the bottom nav with this tab selected
  root?: boolean;            // back button from here exits instead of going back
  render(): string;
  mount?(root: HTMLElement): void;
  unmount?(): void;
  refresh?(): void;          // called when data changes while this screen is open
  actions?: Record<string, Action>;
}

const screens = new Map<string, Screen>();
const globalActions: Record<string, Action> = {};
let current: Screen | null = null;

export function register(...s: Screen[]): void { s.forEach(x => screens.set(x.id, x)); }
export function registerActions(a: Record<string, Action>): void { Object.assign(globalActions, a); }
export const currentScreen = (): Screen | null => current;

function navHtml(tab: NavTab): string {
  const item = (id: NavTab, icon: string) =>
    `<button class="nav-item ${tab === id ? 'on' : ''}" data-action="go" data-to="${id}" aria-current="${tab === id ? 'page' : 'false'}">${icon}${esc(t('nav', id))}</button>`;
  return `<nav id="bottom-nav">${item('home', icons.home)}${item('stats', icons.stats)}${item('profile', icons.user)}</nav>`;
}

export function go(id: string, opts: { replace?: boolean; fromHistory?: boolean } = {}): void {
  const next = screens.get(id);
  if (!next) throw new Error(`unknown screen ${id}`);
  track.setScreen(id); track.daily(`view_${id}`);
  closeSheet();
  current?.unmount?.();
  current = next;
  const app = $('#app')!;
  // keep the persistent 3D canvas alive across renders
  const canvas = app.querySelector<HTMLCanvasElement>('canvas#belly-canvas');
  canvas?.remove();
  app.innerHTML = `<section class="screen active ${next.nav ? 'has-nav' : ''}" id="scr-${next.id}">${next.render()}</section>${next.nav ? navHtml(next.nav) : ''}`;
  if (canvas) document.body.appendChild(Object.assign(canvas, { hidden: true }));
  const root = app.querySelector<HTMLElement>('section.screen')!;
  next.mount?.(root);
  if (!opts.fromHistory) {
    const state = { screen: id };
    if (opts.replace || next.root) history.replaceState(state, '', location.pathname + location.search);
    else history.pushState(state, '', location.pathname + location.search);
  }
  window.scrollTo(0, 0);
}

export function refresh(): void { current?.refresh?.(); }

export function startRouter(): void {
  window.addEventListener('popstate', e => {
    const id = (e.state as { screen?: string } | null)?.screen;
    if (id && screens.has(id)) go(id, { fromHistory: true });
  });

  document.addEventListener('click', e => {
    const el = (e.target as HTMLElement).closest<HTMLElement>('[data-action]');
    if (!el || (el as HTMLButtonElement).disabled) return;
    const name = el.dataset.action!;
    if (el.tagName === 'A') e.preventDefault();
    const handler = sheetHandler(name) ?? current?.actions?.[name] ?? globalActions[name];
    // which buttons people use (name only, never what they typed)
    track.event(`tap_${name === 'go' ? `go_${el.dataset.to ?? ''}` : name}`.replace(/-/g, '_').slice(0, 40));
    if (handler) void handler(el, e);
  });
}

registerActions({
  go: el => go(el.dataset.to!),
  back: () => history.back(),
  'sheet-close': () => closeSheet()
});
